/**
 * Ficha de una palabra guardada (misma vista que {@link LearnedWordDetailPage}).
 */

import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams, useNavigate } from 'react-router-dom';
import { Spinner } from '@/components/atoms/Spinner';
import { VocabularyFlashcard } from '@/components/molecules/VocabularyFlashcard';
import { buildRoute, ROUTES } from '@/constants/routes';
import { useVocabularyStudyTimer } from '@/hooks/useVocabularyStudyTimer';
import { useSavedVocabulary } from '@/context/SavedVocabularyContext';
import { savedVocabularyToVocabItem } from '@/pages/student/LearnedWordsPages/utils/savedVocabularyToVocabItem';
import type { VocabItem } from '@/types/vocabulary.types';
import styles from './LearnedWordVocabLayout.module.scss';

function isPeriod(v: string | undefined): v is 'week' | 'month' {
  return v === 'week' || v === 'month';
}

export function SavedVocabDetailPage() {
  const { t } = useTranslation();
  const { period, savedId } = useParams<{ period: string; savedId: string }>();
  const navigate = useNavigate();
  const { savedItems, removeItem, isSaved, isLoading } = useSavedVocabulary();

  const listPath =
    isPeriod(period) && period
      ? buildRoute(ROUTES.STUDENT.PROGRESS_LEARNED_WORDS, { period })
      : ROUTES.STUDENT.PROGRESS;

  const navigateToSavedList = useCallback(() => {
    navigate(listPath, { state: { learnedWordsSegment: 'saved' as const } });
  }, [navigate, listPath]);

  const savedRow = useMemo(() => {
    if (!savedId) return null;
    return savedItems.find((s) => s.id === savedId) ?? null;
  }, [savedId, savedItems]);

  const vocabItem: VocabItem | null = useMemo(
    () => (savedRow ? savedVocabularyToVocabItem(savedRow) : null),
    [savedRow]
  );

  const isWordReady = Boolean(vocabItem) && !isLoading;

  useVocabularyStudyTimer(isWordReady);

  const handleToggleSaveVocab = useCallback(
    async (v: VocabItem) => {
      if (isSaved(v.word)) {
        await removeItem(v.word);
        navigateToSavedList();
      }
    },
    [isSaved, removeItem, navigateToSavedList]
  );

  if (!isPeriod(period) || !savedId) {
    return <Navigate to={ROUTES.STUDENT.PROGRESS} replace />;
  }

  if (isLoading) {
    return (
      <div className={styles.vocabPage}>
        <div className={styles.vocabPage__state}>
          <Spinner size="lg" aria-label={t('common.loading')} />
        </div>
      </div>
    );
  }

  if (!vocabItem || !savedRow) {
    return (
      <div className={styles.vocabPage}>
        <div className={styles.vocabPage__state}>
          <p>{t('learnedWordsList.wordNotFound')}</p>
          <button
            type="button"
            data-no-press-scale=""
            className={styles.vocabPage__retry}
            onClick={() => navigateToSavedList()}
          >
            {t('learnedWordsList.backToList')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.vocabPage}>
      <div className={styles.vocabPage__card}>
        <VocabularyFlashcard
          className={styles.vocabPage__vocabStretch}
          item={vocabItem}
          isSaved
          onToggleSave={handleToggleSaveVocab}
        />
      </div>
    </div>
  );
}

SavedVocabDetailPage.displayName = 'SavedVocabDetailPage';
